import {FC, Fragment, useCallback, useEffect, useState} from 'react';
import {Transition} from "@headlessui/react";
import {AppDispatch} from "../../../store";
import {useDispatch, useSelector} from "react-redux";
import {callbackSelector, closeCallbackModel} from "../../../store/callback.store.ts";
import {useMutation} from "@apollo/client";
import {SEND_CALLBACK_MUTATION} from "../../../api/graphql/mutations/send-callback.mutation.ts";
import {SendCallbackResponseType} from "../../types/payment/send-callback-response.type.ts";
import {toast} from "react-toastify";
import NotificationToast from "../toasts/notification.toast.tsx";
import {NotificationType} from "../../enums/notification.enum.ts";


const SendCallbackModal: FC = () => {
    const dispatcher: AppDispatch = useDispatch();
    const [show, setShow] = useState<boolean>(false);
    const [callbackUrl, setCallbackUrl] = useState<string>('');
    const {url, operationId} = useSelector(callbackSelector);

    const [sendCallback, sendCallbackData] = useMutation<SendCallbackResponseType>(SEND_CALLBACK_MUTATION);

    useEffect((): void => {
        if (!operationId) {
            setShow(false)
        } else {
            setShow(true)
            setCallbackUrl(url || '');
        }

    }, [operationId, url]);

    useEffect((): void => {
        if (!show) {
            dispatcher(closeCallbackModel(null));
        }
    }, [dispatcher, show]);

    useEffect(() => {
        if (sendCallbackData.error) {
            toast(<NotificationToast type={NotificationType.WARNING} title="Callback" message={sendCallbackData.error.message} />, {
                toastId: 'send-callback-error'
            });
        }
    }, [sendCallbackData.error]);

    useEffect(() => {
        if (sendCallbackData.data) {
            const { message, response } = sendCallbackData.data.sendCallbackOperation;

            toast(<NotificationToast
                type={NotificationType.INFO}
                title={`Callback response`}
                message={response || message}
            />, {
                autoClose: false
            });
        }
    }, [sendCallbackData.data]);

    const send = useCallback(async (): Promise<void> => {
        await sendCallback({
            variables: {
                operationId,
                url: callbackUrl
            }
        });
    }, [operationId, callbackUrl]);

    return (
        <>
            <Transition
                as={Fragment}
                show={show}
                enter="transition ease-out duration-100"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-80"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
            >
                <div className="fixed inset-0 bg-slate-900 bg-opacity-30 z-50 transition-opacity"></div>
            </Transition>
            <Transition
                as={Fragment}
                show={show}
                enter="transition ease-in-out duration-200"
                enterFrom="opacity-0 translate-y-4"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in-out duration-200"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-4"
            >
                <div
                    className="fixed inset-0 z-50 overflow-hidden flex items-center my-4 justify-center transform px-4 sm:px-6">
                    <div onClick={(e) => e.stopPropagation()}
                         className="bg-white rounded shadow-lg overflow-auto max-w-lg w-full max-h-full">

                        {/* Modal header */}
                        <div className="p-4">
                            <div className="text-lg font-semibold text-slate-800">Send callback</div>
                        </div>
                        <div className='divide-y'>
                            <div></div>
                            <div></div>
                        </div>
                        {/* Modal content */}
                        <div className="p-4 pb-0">
                            <div className="text-xs italic mb-6 text-center">
                                When the URL changes, it does not change for the operation!
                            </div>

                            <div className="mt-2">
                                <label className="block text-sm font-medium mb-1" htmlFor="callback_url">URL:</label>
                                <div className="relative">
                                    <input
                                        name="callback_url"
                                        value={callbackUrl}
                                        onChange={e => setCallbackUrl(e.target.value)}
                                        placeholder="Callback URL"
                                        className='form-input w-full max-w-[646px] mr-1'
                                        type="text"
                                    />
                                </div>
                            </div>
                        </div>
                        {/* Modal footer */}

                        <div className="flex justify-end p-4">
                            <button onClick={() => setShow(false)}
                                    className='bg-white border text-gray-700 hover:bg-gray-100 hover btn-sm whitespace-nowrap mr-2'>
                                Cancel
                            </button>
                            <button
                                onClick={send}
                                disabled={sendCallbackData.loading || !callbackUrl}
                                className={`${(sendCallbackData.loading || !callbackUrl) && 'opacity-40'} bg-indigo-500 hover:bg-indigo-600 btn-sm text-white whitespace-nowrap`}>
                                {sendCallbackData.loading
                                    ?
                                    <svg className="animate-spin w-4 h-4 fill-current shrink-0 mr-2" viewBox="0 0 16 16">
                                        <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                                    </svg>
                                    : ''
                                }
                                Send
                            </button>
                        </div>
                    </div>
                </div>
            </Transition>
        </>
    );
};

export default SendCallbackModal;
